// Which palette the marketing site renders in. `globals.css` has carried a
// complete `.dark` token set since the palette was written; this module is the
// switch that selects it.
//
// Ported from the app's devgrail-dev-env/web/src/lib/theme.ts, with one
// deliberate difference, which is the whole of the light/dark policy for this
// site: **an unset preference resolves to light, not to the OS.** The app
// defaults to `system` because you live in it all day and it should follow your
// desk. A marketing page is a first impression, and the light palette -- warm
// ivory, charcoal ink, soft lime -- is the one that impression is designed
// around. Dark is here for the people who want it and for continuity with the
// dashboard, but it is opt-in. `system` is still honoured if something stores
// it; nothing on this site sets it today.
//
// The preference lives in localStorage rather than a cookie on purpose: reading
// a cookie in the root layout opts the whole site out of static prerendering,
// and every page here is prerendered. See
// node_modules/next/dist/docs/01-app/02-guides/preventing-flash-before-hydration.md.

export type Theme = 'light' | 'dark' | 'system'

// Resolved is what actually gets rendered: `system` is a question, not an answer.
export type ResolvedTheme = 'light' | 'dark'

// Read by the pre-paint script in ThemeScript.tsx too, which is a string and
// cannot import this file. The two must agree; see the comment there.
export const THEME_STORAGE_KEY = 'devgrail.theme'

// `storage` only fires in *other* tabs, so a change made here needs an event of
// its own to reach the rest of this page.
export const THEME_CHANGE_EVENT = 'devgrail:theme'

export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

// What an unset, unreadable, or unrecognised preference means here. The one
// line that differs from the app.
export const DEFAULT_THEME: Theme = 'light'

// The `<meta name="theme-color">` values for the two palettes: each one's
// `--background`, so the browser's own chrome continues the page rather than
// sitting on it in a third colour. That is a change of kind, not just of value
// -- light used to ship the brand navy, which was `--primary` and never a
// surface. Hard-coded because the meta tag needs a concrete colour and the token
// is only resolvable once the class is already applied; ThemeScript.tsx
// duplicates the dark one for the same reason.
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#F7F5EF',
  dark: '#211F1D',
}

// Set when localStorage cannot be used (private mode, storage disabled), so the
// control still works for the life of the page instead of appearing to do
// nothing. Null means "storage is the authority".
let inMemory: Theme | null = null

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system'
}

// readTheme reports the stored preference. Anything unrecognised or unreadable
// answers DEFAULT_THEME. Safe to call during SSR, where there is no window and
// the answer is the default the server renders with.
export function readTheme(): Theme {
  if (inMemory !== null) return inMemory
  if (typeof window === 'undefined') return DEFAULT_THEME
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(raw) ? raw : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

// writeTheme persists the choice and tells this page about it. Applying it to
// the DOM is the caller's job -- doing it here as well would mean the class and
// the React state could disagree on which one won.
export function writeTheme(next: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next)
    inMemory = null
  } catch {
    inMemory = next
  }
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT))
}

// prefersDark answers the OS question, defaulting to light where matchMedia is
// missing (jsdom, very old browsers).
export function prefersDark(): boolean {
  try {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches
  } catch {
    return false
  }
}

// resolveTheme turns a preference into the palette to render.
export function resolveTheme(theme: Theme, systemIsDark: boolean = prefersDark()): ResolvedTheme {
  if (theme === 'system') return systemIsDark ? 'dark' : 'light'
  return theme
}

// applyTheme puts the resolved palette on the document. Three things move
// together: the `dark` class `@custom-variant` selects on, `color-scheme` so the
// browser's own widgets (scrollbars, form controls) stop being light islands,
// and the browser chrome colour.
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved])
}
